import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { sanitizeHtml } from '../../utils/safeHtml';

/**
 * O corpo de um texto rico, renderizado com segurança e com as cores do tema.
 *
 * ## Por que existe como componente
 *
 * Isto morava dentro de `NoticeBoardPage` (como `SanitizeHTML`), e passou a ser
 * usado também pela central de ajuda (20/10/2026) — o artigo é exatamente o
 * mesmo problema: HTML escrito por gente, que precisa ser sanitizado e precisa
 * ficar legível nos dois temas.
 *
 * Foi EXTRAÍDO, e não copiado. Duas cópias da mesma regra de cor divergem no
 * primeiro ajuste, e o sintoma seria o artigo legível no mural e cinza-sobre-
 * cinza na central — ou o contrário.
 *
 * ## As cores levam `!important`
 *
 * Não é descuido herdado: o conteúdo vem com estilos embutidos (colado do Word,
 * de e-mail, do editor antigo), e quase sempre com cor fixa escura. Sem forçar,
 * um artigo colado assim fica preto sobre fundo preto no tema escuro.
 */
export const ConteudoHtml = ({ htmlContent, theme }) => {
    /*
     * O tema vem por prop (é como o mural já chamava) OU do próprio MUI.
     *
     * Não é defensividade solta: a central pega o tema de um contexto que pode
     * não estar montado, e sem o segundo caminho a tela inteira some ao abrir
     * um artigo -- `theme.palette` de `undefined` estoura DENTRO do render, e
     * o que sobra é uma área em branco sem erro visível. Foi o que aconteceu,
     * e o teste pegou.
     */
    const temaDoMui = useTheme();
    const t = theme || temaDoMui;
    const tp = t.palette.text.primary;
    const ts = t.palette.text.secondary;

    return (
        <Box
            component="div"
            className="notice-board-html"
            sx={{
                lineHeight: 1.75,
                fontSize: '1rem',
                color: `${tp} !important`,
                '& p, & li, & ul, & ol, & div, & span, & font': { color: `${tp} !important` },
                '& a': { color: `${t.palette.primary.main} !important` },
                '& blockquote': {
                    borderLeft: `4px solid ${t.palette.divider}`,
                    paddingLeft: '1em',
                    margin: '0.5em 0',
                },
                '& blockquote, & blockquote p, & blockquote span': { color: `${ts} !important` },
                '& strong, & b': { fontWeight: '700 !important', color: `${tp} !important` },
                '& em, & i': { fontStyle: 'italic !important' },
                '& u': { textDecoration: 'underline' },
                '& s, & strike': { textDecoration: 'line-through' },
                '& p': { margin: '0 0 0.75em' },
                '& p:last-child': { marginBottom: 0 },
                // A central: imagens de artigo não podem estourar a coluna, e
                // tabelas de procedimento fiscal são largas.
                '& img': { maxWidth: '100%', height: 'auto' },
                '& table': { maxWidth: '100%', borderCollapse: 'collapse' },
                '& td, & th': { border: `1px solid ${t.palette.divider}`, padding: '4px 8px' },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlContent) }}
        />
    );
};

/**
 * Este texto é HTML, ou é texto puro?
 *
 * A central tem os dois: artigo escrito aqui (HTML do editor) e os centenas de
 * `.txt` importados, que são texto corrido. Renderizar `.txt` como HTML
 * colapsaria as quebras de linha e embaralharia um procedimento numerado.
 */
export const pareceHtml = (s) => typeof s === 'string' && /<\/?[a-z][\s\S]*>/i.test(s);

/**
 * O corpo, seja ele qual for.
 *
 * Texto puro mantém as quebras de linha (`pre-wrap`): num `.txt` de
 * procedimento, a numeração dos passos É a formatação.
 */
const ConteudoFormatado = ({ conteudo, theme }) => {
    // Mesma razão do `ConteudoHtml` acima: o tema pode vir por prop ou do MUI.
    const temaDoMui = useTheme();
    const t = theme || temaDoMui;

    if (!conteudo) return null;

    if (pareceHtml(conteudo)) {
        return <ConteudoHtml htmlContent={conteudo} theme={t} />;
    }

    return (
        <Box
            sx={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
                lineHeight: 1.75,
                fontSize: '0.95rem',
                color: t.palette.text.primary,
            }}
        >
            {conteudo}
        </Box>
    );
};

export default ConteudoFormatado;
